<script setup>
import { computed, reactive, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import TextArea from 'dashboard/components-next/textarea/TextArea.vue';
import Switch from 'dashboard/components-next/switch/Switch.vue';

// `pipeline` null = criar; objeto = editar (pré-preenche o form).
const props = defineProps({
  pipeline: { type: Object, default: null },
});
const emit = defineEmits(['submit']);
const { t } = useI18n();

const dialogRef = ref(null);
const form = reactive({
  name: '',
  description: '',
  is_default: false,
  webhook_url: '',
});

const isEdit = computed(() => Boolean(props.pipeline?.id));
const isInvalid = computed(() => !form.name.trim());

const hydrate = () => {
  form.name = props.pipeline?.name ?? '';
  form.description = props.pipeline?.description ?? '';
  form.is_default = props.pipeline?.is_default ?? false;
  form.webhook_url = props.pipeline?.webhook_url ?? '';
};

// open() re-hidrata a partir da prop atual (criar = vazio; editar = dados do pipeline).
const open = () => {
  hydrate();
  dialogRef.value?.open();
};
const close = () => dialogRef.value?.close();

const handleConfirm = () => {
  if (isInvalid.value) return;
  emit('submit', {
    id: props.pipeline?.id ?? null,
    name: form.name.trim(),
    description: form.description.trim(),
    is_default: form.is_default,
    webhook_url: form.webhook_url.trim() || null,
  });
};

defineExpose({ open, close });
</script>

<template>
  <Dialog
    ref="dialogRef"
    width="md"
    :confirm-button-label="
      isEdit ? t('PIPELINES.FORM.SAVE') : t('PIPELINES.FORM.SUBMIT')
    "
    :disable-confirm-button="isInvalid"
    @confirm="handleConfirm"
  >
    <div class="flex flex-col gap-5">
      <header class="flex gap-3 items-center">
        <span
          class="grid flex-shrink-0 place-items-center rounded-xl size-10 bg-n-alpha-2 text-n-brand"
        >
          <span class="size-5 i-lucide-workflow" />
        </span>
        <div class="flex flex-col min-w-0">
          <h3 class="text-base font-medium text-n-slate-12">
            {{
              isEdit
                ? t('PIPELINES.FORM.EDIT_TITLE')
                : t('PIPELINES.FORM.CREATE_TITLE')
            }}
          </h3>
          <p class="text-sm text-n-slate-11">
            {{ t('PIPELINES.FORM.SUBTITLE') }}
          </p>
        </div>
      </header>

      <Input
        v-model="form.name"
        autofocus
        :label="t('PIPELINES.FORM.NAME')"
        :placeholder="t('PIPELINES.FORM.NAME_PLACEHOLDER')"
        :message="t('PIPELINES.FORM.NAME_HINT')"
      />

      <TextArea
        v-model="form.description"
        auto-height
        :label="t('PIPELINES.FORM.DESCRIPTION')"
        :placeholder="t('PIPELINES.FORM.DESCRIPTION_PLACEHOLDER')"
      />

      <!-- Webhook por mudança de etapa -->
      <div class="flex flex-col gap-1.5">
        <span
          class="flex gap-1.5 items-center text-sm font-medium text-n-slate-12"
        >
          <span class="size-4 i-lucide-webhook text-n-teal-10" />
          {{ t('PIPELINES.FORM.WEBHOOK_LABEL') }}
        </span>
        <Input
          v-model="form.webhook_url"
          type="url"
          :placeholder="t('PIPELINES.FORM.WEBHOOK_PLACEHOLDER')"
          :message="t('PIPELINES.FORM.WEBHOOK_HINT')"
        />
      </div>

      <div
        class="flex gap-3 justify-between items-center p-3 rounded-lg outline outline-1 outline-n-weak"
      >
        <div class="flex flex-col min-w-0">
          <span
            class="flex gap-1.5 items-center text-sm font-medium text-n-slate-12"
          >
            <span class="size-4 i-lucide-star text-n-amber-10" />
            {{ t('PIPELINES.FORM.DEFAULT_LABEL') }}
          </span>
          <span class="text-xs text-n-slate-11">
            {{ t('PIPELINES.FORM.DEFAULT_HINT') }}
          </span>
        </div>
        <Switch v-model="form.is_default" />
      </div>
    </div>
  </Dialog>
</template>
